import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, Globe2, X, AtSign, Reply as ReplyIcon } from "lucide-react";
import { API, useAuth } from "../contexts/AuthContext";

/**
 * NotificationBell — polls /me/notifications every 30s (low traffic).
 * For admins, ADDITIONALLY polls /admin/alerts (lecture comments + lecture
 * posts) and merges them into the same bell with distinct icons + deep-links.
 */
export default function NotificationBell({ testid = "notif-bell" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ unread: 0, items: [] });
  const [alerts, setAlerts] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const [readMore, setReadMore] = useState(null); // notification dict when modal open
  const anchorRef = useRef(null);
  const popRef = useRef(null);

  // Smart truncation threshold — short enough to keep the panel scannable,
  // long enough that 90% of typical notifications still fit without a
  // "Read more" hop.
  const PREVIEW_LIMIT = 220;

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/me/notifications`);
      setData(data || { unread: 0, items: [] });
    } catch (_) { /* noop */ }
    if (isAdmin) {
      try {
        const { data } = await axios.get(`${API}/admin/alerts?limit=30`);
        setAlerts(data || { unread: 0, items: [] });
      } catch (_) { /* noop */ }
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => { if (document.hidden) return; load(); }, 120000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
    // eslint-disable-next-line
  }, [isAdmin]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markRead = async (delivery_id) => {
    try { await axios.post(`${API}/me/notifications/${delivery_id}/read`); load(); } catch (_) { /* noop */ }
  };
  const markAll = async () => {
    try {
      await axios.post(`${API}/me/notifications/read-all`);
      if (isAdmin) await axios.post(`${API}/admin/alerts/read-all`);
      load();
    } catch (_) { /* noop */ }
  };
  const markAlertRead = async (alert_id) => {
    try { await axios.post(`${API}/admin/alerts/${alert_id}/read`); load(); } catch (_) { /* noop */ }
  };

  const totalUnread = (data.unread || 0) + (alerts.unread || 0);

  return (
    <div className="relative">
      <button
        ref={anchorRef}
        onClick={() => setOpen(o => !o)}
        className="w-10 h-10 rounded-[12px] border flex items-center justify-center transition relative"
        style={{ borderColor: "var(--px-line)", background: "var(--px-surface)", color: "var(--px-ink-soft)", boxShadow: "var(--px-shadow-xs)" }}
        aria-label="Notifications"
        data-testid={testid}
      >
        <Bell size={15} strokeWidth={1.75} />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
            style={{ background: "var(--accent)", color: "white" }}>
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            ref={popRef}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            className="fixed top-[60px] right-4 w-[360px] max-w-[calc(100vw-32px)] rounded-[14px] overflow-hidden"
            style={{
              background: "var(--px-surface)",
              border: "1px solid var(--px-line-strong)",
              boxShadow: "0 24px 60px -18px rgba(15,23,42,0.30)",
              zIndex: 1000,
            }}
            data-testid="notif-popover"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--px-line)" }}>
              <div className="font-semibold text-sm" style={{ color: "var(--px-ink)" }}>
                {isAdmin ? "Inbox" : "Notifications"}
              </div>
              {totalUnread > 0 && (
                <button onClick={markAll} className="text-[11px] font-semibold" style={{ color: "var(--accent-deep)" }} data-testid="notif-mark-all">
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {/* Admin lecture alerts first */}
              {isAdmin && alerts.items.map(a => {
                const where = [a.subject_name, a.chapter_name, a.lecture_title].filter(Boolean).join(" · ");
                const Icon = a.kind === "lecture_post_new" ? Globe2 : MessageSquare;
                const url = `/student/study?lecture=${a.lecture_id}#comment-${a.comment_id || a.post_id || ""}`;
                return (
                  <div key={a.id}
                    className="px-4 py-3 transition"
                    style={{
                      borderBottom: "1px solid var(--px-line)",
                      background: a.read_at ? "transparent" : "var(--accent-soft)",
                    }}
                    data-testid={`notif-alert-${a.id}`}>
                    <div className="flex items-start gap-2.5">
                      <div className="w-7 h-7 rounded-[8px] flex items-center justify-center shrink-0"
                           style={{ background: "var(--px-rose-soft)", color: "var(--px-rose)" }}>
                        <Icon size={13} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="portal-chip portal-chip-accent" style={{ fontSize: 9 }}>
                            {a.kind === "lecture_post_new" ? "COMMUNITY POST" : "LECTURE COMMENT"}
                          </span>
                          {!a.read_at && <span className="w-2 h-2 rounded-full" style={{ background: "var(--accent)" }} />}
                        </div>
                        <div className="text-[13px] font-semibold" style={{ color: "var(--px-ink)" }}>
                          {a.author_name || a.author_email} <span className="font-normal" style={{ color: "var(--px-ink-mute)" }}>· {where || "Lecture"}</span>
                        </div>
                        <div className="text-[12px] mt-0.5 line-clamp-2" style={{ color: "var(--px-ink-soft)" }}>
                          "{a.text_preview}"
                        </div>
                        <div className="flex items-center gap-3 mt-1.5">
                          <a href={url} className="text-[11px] font-semibold" style={{ color: "var(--accent-deep)" }}
                             onClick={() => !a.read_at && markAlertRead(a.id)}
                             data-testid={`alert-jump-${a.id}`}>
                            Jump to lecture →
                          </a>
                          {!a.read_at && (
                            <button type="button" onClick={() => markAlertRead(a.id)} className="text-[11px]" style={{ color: "var(--px-ink-mute)" }}>
                              Mark read
                            </button>
                          )}
                        </div>
                        <div className="portal-kicker mt-1">{new Date(a.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {data.items.length === 0 && (!isAdmin || alerts.items.length === 0) ? (
                <div className="px-4 py-10 text-center text-sm" style={{ color: "var(--px-ink-mute)" }}>
                  You're all caught up.
                </div>
              ) : data.items.map(n => {
                const isLong = (n.body || "").length > PREVIEW_LIMIT;
                const preview = isLong ? `${n.body.slice(0, PREVIEW_LIMIT - 1)}…` : n.body;
                const isDiscussion = n.type === "discussion_reply" || n.type === "discussion_mention";
                const DType = isDiscussion
                  ? (n.type === "discussion_mention" ? AtSign : ReplyIcon)
                  : null;
                const handleOpen = (e) => {
                  if (!n.read_at) markRead(n.delivery_id);
                  // SPA-navigate internal action_url so React Router handles
                  // it (preserves auth + state). External URLs fall through
                  // to default <a> behaviour.
                  if (n.action_url && n.action_url.startsWith("/")) {
                    e.preventDefault();
                    setOpen(false);
                    navigate(n.action_url);
                  }
                };
                return (
                <div key={n.delivery_id}
                  className="px-4 py-3 transition"
                  style={{
                    borderBottom: "1px solid var(--px-line)",
                    background: n.read_at ? "transparent" : "var(--accent-soft)",
                  }}
                  data-testid={`notif-item-${n.delivery_id}`}>
                  <div className="flex items-start gap-2.5">
                    {DType && (
                      <div className="w-7 h-7 rounded-[8px] flex items-center justify-center shrink-0"
                           style={{
                             background: n.type === "discussion_mention" ? "#fef3c7" : "#dbeafe",
                             color: n.type === "discussion_mention" ? "#92400e" : "#1e40af",
                           }}>
                        <DType size={13} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="portal-chip portal-chip-accent" style={{ fontSize: 9 }}>
                          {n.type === "discussion_reply" ? "REPLY"
                            : n.type === "discussion_mention" ? "MENTION"
                            : n.type}
                        </span>
                        {!n.read_at && <span className="w-2 h-2 rounded-full" style={{ background: "var(--accent)" }} />}
                      </div>
                      <div className="text-sm font-semibold" style={{ color: "var(--px-ink)" }}>{n.title}</div>
                      <div className="text-xs mt-0.5 whitespace-pre-wrap" style={{ color: "var(--px-ink-soft)" }}>
                        {preview}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {isLong && (
                          <button type="button"
                            onClick={() => { setReadMore(n); if (!n.read_at) markRead(n.delivery_id); }}
                            className="text-[11px] font-semibold"
                            style={{ color: "var(--accent-deep)" }}
                            data-testid={`notif-read-more-${n.delivery_id}`}>
                            Read more
                          </button>
                        )}
                        {n.action_url && (
                          <a href={n.action_url}
                             className="text-[11px] font-semibold"
                             style={{ color: "var(--accent-deep)" }}
                             onClick={handleOpen}
                             data-testid={`notif-action-${n.delivery_id}`}>
                            {n.action_label || "Open"} →
                          </a>
                        )}
                      </div>
                      <div className="portal-kicker mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Read More modal — rendered into a portal anchored at document.body
          so it escapes any ancestor stacking context (e.g. the StudentHome
          hero's `transform`/`backdrop-filter` blocks) that was capping the
          modal's effective z-index and placing it behind the dashboard. */}
      {typeof document !== "undefined" && createPortal(
        <AnimatePresence>
          {readMore && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
              style={{ background: "rgba(15,23,42,0.40)", zIndex: 2147483600 }}
              data-testid="notif-readmore-modal"
              onClick={() => setReadMore(null)}
            >
              <motion.div
                initial={{ y: 12, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 12, opacity: 0, scale: 0.98 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-[560px] rounded-2xl flex flex-col my-auto"
                style={{
                  background: "var(--px-surface)",
                  border: "1px solid var(--px-line-strong)",
                  boxShadow: "0 30px 60px -20px rgba(15,23,42,0.40)",
                  maxHeight: "min(85dvh, 85vh)",
                }}
              >
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b shrink-0" style={{ borderColor: "var(--px-line)" }}>
                  <div className="min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.16em] font-semibold mb-1" style={{ color: "var(--accent-deep)" }}>
                      {readMore.type === "discussion_reply" ? "Reply"
                        : readMore.type === "discussion_mention" ? "Mention"
                        : readMore.type}
                    </div>
                    <div className="text-[17px] font-semibold leading-tight" style={{ color: "var(--px-ink)" }}>
                      {readMore.title}
                    </div>
                    <div className="text-[11.5px] mt-1" style={{ color: "var(--px-ink-mute)" }}>
                      {new Date(readMore.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button onClick={() => setReadMore(null)}
                          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: "var(--px-bg-subtle)", color: "var(--px-ink-mute)" }}
                          data-testid="notif-readmore-close">
                    <X size={14} />
                  </button>
                </div>
                <div className="px-5 py-4 text-[14px] leading-relaxed whitespace-pre-wrap flex-1 overflow-y-auto"
                     style={{ color: "var(--px-ink-soft)", WebkitOverflowScrolling: "touch" }}
                     data-testid="notif-readmore-body">
                  {readMore.body}
                </div>
                {readMore.action_url && (
                  <div className="px-5 py-3 border-t shrink-0" style={{ borderColor: "var(--px-line)", background: "var(--px-bg-subtle)" }}>
                    <button
                      type="button"
                      onClick={() => {
                        const url = readMore.action_url;
                        setReadMore(null);
                        setOpen(false);
                        if (url.startsWith("/")) navigate(url);
                        else window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      className="px-4 py-2 rounded-full text-[13px] font-semibold press-btn"
                      style={{ background: "var(--accent)", color: "white" }}
                      data-testid="notif-readmore-action">
                      {readMore.action_label || "Open"} →
                    </button>
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
